import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EntityView from '../../components/entity/EntityView';
import Header from '../../components/Header';
import { translateIntolerances, translateDietaryPreferences } from '../../utils/translate';
import { calculateBMR, calculateDEJ, getNAPLabel, getPeriodLabel } from '../../utils/calculateMacros';
import { Box, CircularProgress } from '@mui/material';

const UserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour récupérer les données utilisateur
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des détails de l\'utilisateur.');
        }

        const data = await response.json();
        setUser(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // Sections à afficher dans EntityView
  const renderSections = () => {
    if (!user) return [];

    // Transformation du genre pour l'affichage en français
    const genderLabel = user.gender
      ? user.gender === 'male'
        ? 'Masculin'
        : user.gender === 'female'
        ? 'Féminin'
        : 'Autre'
      : 'Non renseigné';

    // Calcul V2.0 : BMR et DEJ
    const bmr = calculateBMR(user);
    const nap = user.sportProfile?.nap || 1.5;
    const dej = bmr ? calculateDEJ(bmr, nap) : null;
    const period = user.sportProfile?.period || 'base';

    const sections = [
      {
        title: 'Informations personnelles',
        fields: [
          { label: 'Nom complet', value: `${user.firstName} ${user.lastName}` },
          { label: 'Email', value: user.email },
          { label: 'Téléphone', value: user.phone || 'Non renseigné' },
          { label: 'Genre', value: genderLabel },
        ],
      },
      {
        title: 'Données physiques',
        fields: [
          { label: 'Âge', value: user.age ? `${user.age} ans` : 'Non renseigné' },
          { label: 'Poids', value: user.weight ? `${user.weight} kg` : 'Non renseigné' },
          { label: 'Taille', value: user.height ? `${user.height} cm` : 'Non renseigné' },
        ],
      },
    ];

    // Profil sportif (si disponible)
    if (user.sportProfile || bmr) {
      sections.push({
        title: 'Profil sportif',
        fields: [
          {
            label: 'Niveau d\'activité quotidien',
            value: `NAP ${nap} - ${getNAPLabel(nap)}`
          },
          {
            label: 'Période d\'entraînement',
            value: getPeriodLabel(period)
          },
          {
            label: 'Métabolisme de base',
            value: bmr ? `${bmr} kcal/jour` : 'Non calculable'
          },
          {
            label: 'Dépense énergétique journalière',
            value: dej ? `${dej} kcal/jour` : 'Non calculable'
          },
        ],
      });
    }

    sections.push(
      {
        title: 'Objectifs et santé',
        fields: [
          { label: 'Objectifs', value: user.goals || 'Non renseigné' },
          { label: 'Conditions médicales', value: user.medicalConditions?.length ? user.medicalConditions.join(', ') : 'Aucune' },
          { label: 'Intolérances alimentaires', value: user.intolerances?.length ? translateIntolerances(user.intolerances).join(', ') : 'Aucune' },
        ],
      },
      {
        title: 'Régime alimentaire',
        fields: [
          { label: 'Préférences alimentaires', value: user.dietaryPreferences?.length ? translateDietaryPreferences(user.dietaryPreferences).join(', ') : 'Aucune' },
        ],
      }
    );

    return sections;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Erreur : {error}</div>
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Box>
        <EntityView
          title="Détails de l'utilisateur"
          sections={renderSections()}
          navigateBack={() => navigate('/users')}
          onEdit={() => navigate(`/users/edit/${id}`)}
        />
      </Box>
    </>
  );
};

export default UserView;
